<template>
  <div
    tabindex="1"
    class="select-container"
    @focus="showOptions"
    @focusout="handleBlur"
  >
    <div
      ref="mainContainer"
      :class="['p-3 w-full flex flex-row border-transparent rounded-lg cursor-pointer', containerClass]"
    >
      <div class="w-full flex flex-nowrap items-center overflow-x-auto h-full hide-scrollbar">
        <span
          v-if="!selected.length"
          class="w-11/12"
          :class="[selected.length && !disabled ? 'text-grey' : 'text-grey-001']"
        >
          <div
            v-if="separator && isAddOneByOne"
            class="flex flex-row items-center justify-start"
          >
            <vs-button
              type="filled"
              color="secondary"
              class="rounded-xl h-4 w-4 flex flex-row justify-center items-center cursor-pointer"
              @click="showOptions"
            >
              <vs-icon
                icon="add"
                size="1rem"
                color="#FFFFFF"
                class="flex flex-row justify-center items-center"
              />
            </vs-button>
          </div>
          <span v-else>{{ placeholder }}</span>
        </span>
        <div
          v-else
          class="flex items-center"
        >
          <div
            v-for="(item, index) of selected"
            :key="index"
            class="flex items-center justify-start flex-shrink-0"
          >
            <div :class="[disabled ? 'bg-disabled-tag' : 'bg-tag']">
              <span :class="[disabled ? 'text-grey' : 'text-primary']">{{ item.text }}</span>
              <vs-icon
                icon="close"
                size="10px"
                class="ml-1"
                :color="disabled ? '#3D3D3D': '#493493'"
                @click="removeItem(item)"
              />
            </div>
            <span
              v-if="index < selected.length - 1"
              class="separator"
            >{{ separator }}</span>
          </div>
          <div
            v-if="separator && isAddOneByOne"
            class="flex flex-row items-center justify-start"
          >
            <span
              class="separator"
            >{{ separator }}</span>
            <vs-button
              type="filled"
              color="secondary"
              class="rounded-xl h-4 w-4 flex flex-row justify-center items-center cursor-pointer"
              @click="showOptions"
            >
              <vs-icon
                icon="add"
                size="1rem"
                color="#FFFFFF"
                class="flex flex-row justify-center items-center"
              />
            </vs-button>
          </div>
        </div>
      </div>
      <div class="w-1/12 flex flex-row items-center justify-end">
        <vs-icon
          :icon="isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down'"
          :color="disabled ? '#AAAAAA': '#493493'"
        />
      </div>
    </div>
    <div
      v-show="isOptionShown"
      class="bg-option-container"
    >
      <div
        v-if="activateSearch"
        class="p-3"
        :class="[options.length ? 'bg-option-input-container' : 'bg-option-input-disabled-container']"
      >
        <div class="flex flex-grow flex-col items-start justify-center relative">
          <vs-icon
            class="icon-search"
            icon="search"
            :color="options.length ? '#493493' : '#AAAAAA'"
          />
          <input
            ref="searchInput"
            v-model="searchQuery"
            placeholder="Search here..."
            class="bg-option-input"
            :disabled="!options.length"
          >
        </div>
      </div>
      <div
        class="bg-inner-option-container"
        :class="allowDuplicateValues ? 'h-full':''"
      >
        <div v-if="!grouped">
          <div
            v-if="!filteredOptions.length"
            class="flex flex-row items-center justify-center"
          >
            <span class="empty-option">Nothing can be shown</span>
          </div>
          <ul v-else>
            <li
              v-for="(item, index) of filteredOptions"
              class="my-1"
              :key="index"
              @click="selectItem(item)"
            >
              <div class="flex flex-row">
                <div class="w-11/12 flex flex-row items-center">
                  <span>{{ item.text }}</span>
                </div>
                <div
                  v-if="!allowDuplicateValues"
                  class="w-1/12 flex flex-row items-center justify-end"
                >
                  <transition name="spin">
                    <div
                      class="p-1 flex flex-row border border-secondary rounded-md"
                      :class="[checkMarkItem(item) ? 'bg-secondary' : 'bg-white']"
                    >
                      <vs-icon
                        icon="check"
                        color="#FFFFFF"
                        size="8px"
                      />
                    </div>
                  </transition>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else>
          <div
            v-if="!groupedFilteredOptions.length"
            class="flex flex-row items-center justify-center"
          >
            <span class="empty-option">Nothing can be shown</span>
          </div>
          <ul v-else>
            <template v-for="(group, groupIndex) of groupedFilteredOptions">
              <li
                v-if="group.title"
                class="font-bold cursor-default"
                :key="groupIndex"
              >
                {{ group.title }}
              </li>
              <li
                v-for="(item, index) in group.options"
                class="my-1"
                :key="`${groupIndex}-${index}`"
                @click="selectItem(item)"
              >
                <div class="flex flex-row">
                  <div class="w-11/12 flex flex-row items-center">
                    <span>{{ item.text }}</span>
                  </div>
                  <div
                    v-if="!allowDuplicateValues"
                    class="w-1/12 flex flex-row items-center justify-end"
                  >
                    <transition name="spin">
                      <div
                        class="p-1 flex flex-row border border-secondary rounded-md"
                        :class="[checkMarkItem(item) ? 'bg-secondary' : 'bg-white']"
                      >
                        <vs-icon
                          icon="check"
                          color="#FFFFFF"
                          size="8px"
                        />
                      </div>
                    </transition>
                  </div>
                </div>
              </li>
            </template>
          </ul>
        </div>
        <div
          v-if="isCanAdd && searchQuery.trim() && !isOptionExists(searchQuery)"
          class="add-option-container"
        >
          <vs-button
            type="filled"
            color="secondary"
            class="w-full rounded-lg font-semibold"
            @click="handleAdd"
          >
            Add "{{ searchQuery }}"
          </vs-button>
        </div>
      </div>
      <div
        v-if="!allowDuplicateValues"
        class="bg-inner-footer-container"
      >
        <vs-button
          type="filled"
          color="secondary"
          class="w-32 rounded-lg font-semibold"
          :disabled="!options.length"
          @click="confirmItem"
        >
          Select
        </vs-button>
      </div>
    </div>
    <div
      v-if="danger && !selected.length"
      class="text-danger text-xs mt-1"
    >
      {{ dangerText }}
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * `value` or `v-model` will returned
     * `[{ value: 'something', text: 'something' }]`
     */
    value: {
      type: null,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => 'Select option'
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    isValid: {
      type: Boolean,
      default: () => true
    },
    /**
     * Array of available options: Objects.
     * Objects must consist of `value` and `text`
     * example: `{ value: 'something', text: 'something' }`
     */
    options: {
      type: Array,
      default: () => []
    },
    activateSearch: {
      type: Boolean,
      default: () => false
    },
    isCanAdd: {
      type: Boolean,
      default: () => false
    },
    onAdd: {
      type: Function,
      default: () => {
      }
    },
    separator: {
      type: String,
      default: () => null
    },
    isAddOneByOne: {
      type: Boolean,
      default: () => false
    },
    allowDuplicateValues: {
      type: Boolean,
      default: () => false
    },
    grouped: {
      type: Boolean,
      default: () => false
    },
    danger: {
      type: Boolean,
      default: () => false
    },
    dangerText: {
      type: String,
      default: () => '* Required'
    },
    isBusy: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      isOptionShown: false,
      selected: this.value,
      tempSelected: [],
      searchQuery: ''
    }
  },
  computed: {
    filteredOptions() {
      if (!this.activateSearch || !this.searchQuery.trim()) {
        return this.options
      }
      return this.options.filter(option =>
        option.text.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    groupedFilteredOptions() {
      if (!this.activateSearch || !this.searchQuery.trim()) {
        return this.options
      }

      return this.options
        .map(group => ({
          title: group.title,
          options: group.options.filter(option => option.text.toLowerCase().includes(this.searchQuery.toLowerCase()))
        }))
        .filter(group => group.options.length > 0)
    },
    containerClass() {
      return {
        'bg-accent-multiselect': !this.disabled && !this.danger,
        'bg-accent-disabled-multiselect': this.disabled,
        'bg-accent-danger-multiselect': !this.disabled && this.danger,
        'border-danger border-1': this.danger && !this.isOptionShown,
        'border-secondary border-2': this.isOptionShown,
        'animate-bounce cursor-not-allowed': this.isBusy
      }
    }
  },
  watch: {
    value: {
      handler(val) {
        this.selected = val
      }
    },
    isValid: {
      handler(val) {
        if (!this.disabled) {
          if (val) {
            this.$refs.mainContainer.classList.remove('bg-accent-danger-multiselect')
            this.$refs.mainContainer.classList.add('bg-accent-multiselect')
            return
          }

          this.$refs.mainContainer.classList.remove('bg-accent-multiselect')
          this.$refs.mainContainer.classList.add('bg-accent-danger-multiselect')
          return
        }
        this.$refs.mainContainer.classList.add('bg-accent-disabled-multiselect')
      }
    }
  },
  methods: {
    showOptions() {
      if (!this.disabled && !this.isOptionShown) {
        /**
         * if `selected` is not mapped,
         * `selected` will be following `tempSelected`
         * when `tempSelected` react to data change
         */
        this.tempSelected = this.selected.map(item => item)
        this.isOptionShown = true

        // Calculate the position and height of the dropdown
        this.$nextTick(() => {
          const searchInput = this.$refs.searchInput
          if (searchInput) {
            searchInput.focus()
          }

          const dropdown = this.$el.querySelector('.bg-option-container')
          const dropdownInner = this.$el.querySelector('.bg-inner-option-container')
          const inputRect = this.$el.getBoundingClientRect()
          const dropdownHeight = dropdown.offsetHeight

          // Find the vs-con-table container
          const vsConTable = this.$el.closest('.vs-con-table')
          let containerHeight = window.innerHeight
          let containerTop = 0

          if (vsConTable) {
            const tableRect = vsConTable.getBoundingClientRect()
            containerHeight = tableRect.height
            containerTop = tableRect.top
          }

          // Calculate space above and below relative to the container
          const spaceBelow = containerHeight - (inputRect.bottom - containerTop)
          const spaceAbove = inputRect.top - containerTop

          // Determine the best position and max height
          if (spaceAbove > spaceBelow) {
            // Position above the input
            dropdown.style.top = 'auto'
            dropdown.style.bottom = '100%'
            dropdown.style.maxHeight = `${spaceAbove - 30}px` // Subtract a small buffer
            dropdownInner.style.maxHeight = `${spaceAbove - 80}px` // Subtract a small buffer
          } else {
            // Position below the input
            dropdown.style.top = '100%'
            dropdown.style.bottom = 'auto'
            dropdown.style.maxHeight = `${spaceBelow - 30}px` // Subtract a small buffer
            dropdownInner.style.maxHeight = `${spaceBelow - 80}px` // Subtract a small buffer
          }
        })

        this.$el.focus()
      }
    },
    closeOptions() {
      if (!this.disabled && this.isOptionShown) {
        this.isOptionShown = false
        this.$el.blur()
      }
    },
    handleBlur(ev) {
      if (!ev.currentTarget.contains(ev.relatedTarget)) {
        this.closeOptions()
      }
    },
    selectItem(item) {
      if (this.allowDuplicateValues) {
        this.tempSelected.push(item)
        this.confirmItem()
      } else {
        const isItemHasSelected = this.tempSelected.find(selectedItem => item.value === selectedItem.value)
        if (isItemHasSelected) {
          this.tempSelected = this.tempSelected.filter(selectedItem => selectedItem.value !== item.value)
        } else {
          this.tempSelected.push(item)
        }
      }

      if (this.isAddOneByOne) {
        this.confirmItem()
        this.closeOptions()
      }
    },
    checkMarkItem(option) {
      if (this.allowDuplicateValues) {
        return false
      }
      const existedItem = this.tempSelected.find(selectedItem => selectedItem.value === option.value)
      return !!existedItem
    },
    removeItem(item) {
      if (this.disabled) {
        return
      }

      this.isOptionShown = false
      this.$el.blur()

      this.selected = this.selected.filter(selectedItem => selectedItem.value !== item.value)
      this.$emit('input', this.selected)
    },
    confirmItem() {
      this.selected = this.tempSelected
      this.$emit('input', this.selected)
    },
    isOptionExists(searchTerm) {
      return !this.grouped
        ? this.options.some(option => option.text.toLowerCase() === searchTerm.toLowerCase())
        : this.options.filter((option) => option.options.some((item) => item.text.toLowerCase() === searchTerm.toLowerCase())).length > 0
    },
    handleAdd() {
      if (this.searchQuery.trim()) {
        this.onAdd(this.searchQuery)
        this.searchQuery = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  @apply p-3 cursor-pointer;

  color: #3D3D3D;
}

.select-container {
  @apply w-full relative;
}

.bg-accent-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-accent-disabled-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-not-allowed;

  height: calc(1rem * 10 / 3);
  background-color: #ECEBED;
}

.bg-accent-danger-multiselect {
  @apply p-3 w-full flex flex-row border-2 border-danger rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-option-container {
  @apply mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg;
  z-index: 1000;
  height: 20rem;

  top: 100%;
  left: 0;
  transform: translateY(0.25rem);
}

.bg-inner-option-container {
  @apply p-3 flex flex-col overflow-auto;

  height: 15rem;
}

.bg-inner-footer-container {
  @apply p-3 flex flex-row items-center justify-end shadow-inner;
}

.bg-tag, .bg-disabled-tag {
  @apply px-2 py-1 mr-2 flex flex-row items-center text-xs rounded;

  background-color: #CDC6E1;
}

.bg-disabled-tag {
  @apply bg-grey-001;
}

.empty-option {
  @apply italic text-center;

  color: #3D3D3D;
}

.spin-enter-active {
  animation: spin 0.5s;
}

.spin-leave-active {
  opacity: 0;
}

.border-1 {
  border-width: 1px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(90deg);
  }

  100% {
    transform: rotate(0deg);
  }
}

.search-container {
  @apply flex;
  @apply p-1;
  @apply w-full;
}

.add-option-container {
  @apply mt-3;
}

.separator {
  @apply mr-2;
}

.bg-option-input-container {
  @apply flex flex-row items-center rounded-lg;

  min-height: 3rem;
}

.bg-option-input-disabled-container {
  @apply flex flex-row items-center rounded-lg;

  min-height: 3rem;
  background-color: #ECEBED;
}

.bg-option-input {
  @apply w-full bg-transparent p-4;
  border-radius: 0.5rem !important;
  padding-left: calc(1rem + 24px) !important;
  background-color: #F1F1FD;

  &:disabled {
    @apply cursor-not-allowed;
  }
}

.icon-search {
  @apply absolute cursor-default;

  top: auto;
  left: 0.5rem;
}

.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
